import React, { FC, memo, useRef, useCallback, useState, useMemo } from 'react';
import { getActions, withGlobal } from '../../global';

import {
  ApiAttachment,
  MAIN_THREAD_ID,
  type ApiChat,
  type ApiTopic,
} from '../../api/types';

import { REM } from './helpers/mediaDimensions';
import {
  CHAT_SMALL_HEIGHT_PX,
  EDITABLE_INPUT_CSS_SELECTOR,
  EDITABLE_INPUT_ID,
  EDITABLE_INPUT_MODAL_ID,
} from '../../config';
import renderText from './helpers/renderText';
import { getCanPostInChat, isUserId } from '../../global/helpers';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import useKeyboardListNavigation from '../../hooks/useKeyboardListNavigation';
import useInputFocusOnOpen from '../../hooks/useInputFocusOnOpen';

import Loading from '../ui/Loading';
import Modal from '../ui/Modal';
import InputText from '../ui/InputText';
import Button from '../ui/Button';
import InfiniteScroll from '../ui/InfiniteScroll';
import ListItem from '../ui/ListItem';
import GroupChatInfo from './GroupChatInfo';
import PrivateChatInfo from './PrivateChatInfo';
import Transition from '../ui/Transition';
import TopicIcon from './TopicIcon';
import { useTranslation } from 'react-i18next';
import IconSvg from '../ui/IconSvg';
import NothingFound from './NothingFound';
import useLastCallback from '../../hooks/useLastCallback';
import SearchInput from '../ui/SearchInput';
import DropdownMenu from '../ui/DropdownMenu';

import './ChatOrUserPicker.scss';
import MenuItem from '../ui/MenuItem';
import SymbolMenuButton from '../middle/composer/SymbolMenuButton';
import MessageInput from '../middle/composer/MessageInput';
import { selectCurrentLimit } from '../../global/selectors/limits';
import useAppLayout from '../../hooks/useAppLayout';
import useFlag from '../../hooks/useFlag';
import { isSelectionInsideInput } from '../middle/composer/helpers/selection';
import { insertHtmlInSelection } from '../../util/selection';
import focusEditableElement from '../../util/focusEditableElement';
import { TabState } from '../../global/types';
import { selectTabState } from '../../global/selectors';
import { copyTextToClipboard } from '../../util/clipboard';
import Checkbox from '../ui/Checkbox';
import PickerSelectedItem from './PickerSelectedItem';
import parseMessageInput from '../../util/parseMessageInput';
import buildAttachment from '../middle/composer/helpers/buildAttachment';

export type OwnProps = {
  currentUserId?: string;
  chatOrUserIds: string[];
  selectedIds: string[];
  chatsById?: Record<string, ApiChat>;
  isOpen: boolean;
  searchPlaceholder: string;
  search: string;
  loadMore?: NoneToVoidFunction;
  onSearchChange: (search: string) => void;
  onSelectChatOrUser: (chatOrUserId: string, threadId?: number) => void;
  onClose: NoneToVoidFunction;
  onCloseAnimationEnd?: NoneToVoidFunction;
};

type StateProps = {
  captionLimit: number;
  requestedShare: TabState['requestedShare'];
};

const CHAT_LIST_SLIDE = 0;
const TOPIC_LIST_SLIDE = 1;
const TOPIC_ICON_SIZE = 2.75 * REM;
const SEND_MODAL_INPUT_ID = 'comment-input-text';
const MAX_FULL_ITEMS = 6;
const ALWAYS_FULL_ITEMS_COUNT = 3;

const ChatOrUserPicker: FC<OwnProps & StateProps> = ({
  isOpen,
  currentUserId,
  chatOrUserIds,
  chatsById,
  search,
  searchPlaceholder,
  requestedShare,
  captionLimit,
  selectedIds,
  loadMore,
  onSearchChange,
  onSelectChatOrUser,
  onClose,
  onCloseAnimationEnd,
}) => {
  const {
    loadTopics,
    setGlobalSearchQuery,
    showNotification,
    multiSendMessage,
  } = getActions();
  const { isMobile } = useAppLayout();
  const { t } = useTranslation();
  const { link, file } = requestedShare || {};
  const isShareLink = Boolean(link || file);
  const containerRef = useRef<HTMLDivElement>(null);
  const topicContainerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const topicSearchRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const [viewportIds, getMore] = useInfiniteScroll(
    loadMore,
    chatOrUserIds,
    Boolean(search)
  );

  const shouldMinimize = selectedIds && selectedIds.length > MAX_FULL_ITEMS;

  const [getHtml, setHtml] = useState('');
  const [forumId, setForumId] = useState<string | undefined>(undefined);
  const [topicSearch, setTopicSearch] = useState<string>('');
  const activeKey = forumId ? TOPIC_LIST_SLIDE : CHAT_LIST_SLIDE;
  const viewportOffset = chatOrUserIds!.indexOf(viewportIds![0]);

  const [isSymbolMenuOpen, openSymbolMenu, closeSymbolMenu] = useFlag();

  const resetSearch = useLastCallback(() => {
    onSearchChange('');
    setGlobalSearchQuery({ query: '' });
  });
  useInputFocusOnOpen(
    searchRef,
    isOpen && activeKey === CHAT_LIST_SLIDE,
    resetSearch
  );
  useInputFocusOnOpen(topicSearchRef, isOpen && activeKey === TOPIC_LIST_SLIDE);

  const [topicIds, topics] = useMemo(() => {
    const topicsResult = forumId ? chatsById?.[forumId].topics : undefined;
    if (!topicsResult) {
      return [undefined, undefined];
    }

    const searchTitle = topicSearch.toLowerCase();

    const result = topicsResult
      ? Object.values(topicsResult).reduce((acc, topic) => {
          if (
            getCanPostInChat(chatsById![forumId!], topic.id) &&
            (!searchTitle || topic.title.toLowerCase().includes(searchTitle))
          ) {
            acc[topic.id] = topic;
          }

          return acc;
        }, {} as Record<number, ApiTopic>)
      : topicsResult;

    return [Object.keys(result).map(Number), result];
  }, [chatsById, forumId, topicSearch]);

  const handleHeaderBackClick = useCallback(() => {
    setForumId(undefined);
    setTopicSearch('');
  }, []);

  const handleSearchChange = useLastCallback((value: string) => {
    onSearchChange(value);
    setGlobalSearchQuery({ query: value });
  });

  const handleTopicSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTopicSearch(e.currentTarget.value);
    },
    []
  );

  const handleKeyDown = useKeyboardListNavigation(
    containerRef,
    isOpen,
    (index) => {
      if (viewportIds && viewportIds.length > 0) {
        const chatId = viewportIds[index === -1 ? 0 : index];
        const chat = chatsById?.[chatId];
        if (chat?.isForum) {
          if (!chat.topics) loadTopics({ chatId });
          setForumId(chatId);
        } else {
          onSelectChatOrUser(chatId);
        }
      }
    },
    '.ListItem-button',
    true
  );

  const handleTopicKeyDown = useKeyboardListNavigation(
    topicContainerRef,
    isOpen,
    (index) => {
      if (topicIds?.length) {
        onSelectChatOrUser(forumId!, topicIds[index === -1 ? 0 : index]);
      }
    },
    '.ListItem-button',
    true
  );

  const handleClick = useCallback(
    (e: React.MouseEvent, chatId: string) => {
      const chat = chatsById?.[chatId];
      if (chat?.isForum) {
        if (!chat.topics) loadTopics({ chatId });
        setForumId(chatId);
        resetSearch();
      } else {
        onSelectChatOrUser(chatId);
      }
    },
    [chatsById, loadTopics, onSelectChatOrUser, resetSearch]
  );

  function copy(text: string, entity: string) {
    copyTextToClipboard(text);
    showNotification({ message: `${entity} ${t('Copy.WasCopied')}` });
  }

  const triggerComponent: FC<{ onTrigger: () => void; isOpen?: boolean }> =
    useMemo(() => {
      return ({ onTrigger }) => (
        <Button
          round
          color='translucent'
          size='smaller'
          ariaLabel={String(t('Export'))}
          onClick={onTrigger}
        >
          <i className='icon-svg'>
            <IconSvg name='export' />
          </i>
        </Button>
      );
    }, []);

  const handleTopicClick = useCallback(
    (e: React.MouseEvent, topicId: number) => {
      onSelectChatOrUser(forumId!, topicId);
    },
    [forumId, onSelectChatOrUser]
  );

  const handleSendClick = useLastCallback(async () => {
    let newText = getHtml;
    let attachment: ApiAttachment | undefined;

    if (link && !file) {
      newText = `${link}<br>${getHtml}`;
    } else if (file) {
      attachment = await buildAttachment(file.name, file);
    }
    const { text } = parseMessageInput(newText);
    multiSendMessage({ chatIds: selectedIds, text, attachment });
    onClose();
  });

  const insertHtmlAndUpdateCursor = useCallback(
    (newHtml: string, inputId: string = EDITABLE_INPUT_ID) => {
      const selection = window.getSelection()!;
      let messageInput: HTMLDivElement;
      if (inputId === EDITABLE_INPUT_ID) {
        messageInput = document.querySelector<HTMLDivElement>(
          EDITABLE_INPUT_CSS_SELECTOR
        )!;
      } else {
        messageInput = document.getElementById(inputId) as HTMLDivElement;
      }

      if (selection.rangeCount) {
        const selectionRange = selection.getRangeAt(0);
        if (isSelectionInsideInput(selectionRange, inputId)) {
          insertHtmlInSelection(newHtml);
          messageInput.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
      }

      setHtml(`${getHtml}${newHtml}`);

      // If selection is outside of input, set cursor at the end of input
      requestAnimationFrame(() => {
        focusEditableElement(messageInput);
      });
    },
    [getHtml, setHtml]
  );

  const insertTextAndUpdateCursor = useCallback(
    (text: string) => {
      const newHtml = renderText(text, ['escape_html', 'emoji_html', 'br_html'])
        .join('')
        .replace(/\u200b+/g, '\u200b');
      insertHtmlAndUpdateCursor(newHtml, EDITABLE_INPUT_MODAL_ID);
    },
    [insertHtmlAndUpdateCursor]
  );

  function renderHeader() {
    return activeKey === TOPIC_LIST_SLIDE ? (
      <>
        <Button
          round
          color='translucent'
          size='smaller'
          ariaLabel={String(t('Back'))}
          onClick={handleHeaderBackClick}
        >
          <i className='icon-arrow-left' />
        </Button>
        <InputText
          elRef={topicSearchRef}
          value={topicSearch}
          name='forward'
          onChange={handleTopicSearchChange}
          onKeyDown={handleTopicKeyDown}
          placeholder={searchPlaceholder}
        />
      </>
    ) : isShareLink ? (
      <>
        <Button
          round
          color='translucent'
          size='smaller'
          ariaLabel={String(t('Close'))}
          onClick={onClose}
        >
          <i className='icon-svg'>
            <IconSvg name='close' />
          </i>
        </Button>
        <SearchInput
          inputRef={searchRef}
          value={search}
          name='forward'
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
        />
        <DropdownMenu trigger={triggerComponent} positionX='right'>
          <MenuItem
            onClick={() => copy(String(link), 'Link')}
            customIcon={<IconSvg name='copy' w='25' h='25' />}
          >
            Copy Link
          </MenuItem>
        </DropdownMenu>

        <div className='selected-wrap'>
          {selectedIds.map((id, i) => (
            <PickerSelectedItem
              key={id}
              chatOrUserId={id}
              isMinimized={
                shouldMinimize &&
                i < selectedIds.length - ALWAYS_FULL_ITEMS_COUNT
              }
              canClose
              onClick={() => onSelectChatOrUser(id)}
              clickArg={id}
            />
          ))}
        </div>
      </>
    ) : (
      <>
        <Button
          round
          color='translucent'
          size='smaller'
          ariaLabel={String(t('Close'))}
          onClick={onClose}
        >
          <i className='icon-svg'>
            <IconSvg name='close' />
          </i>
        </Button>
        <SearchInput
          inputRef={searchRef}
          value={search}
          name='forward'
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
        />
      </>
    );
  }

  function renderTopicList() {
    return (
      <>
        <InfiniteScroll
          elRef={topicContainerRef}
          className='picker-list custom-scroll'
          items={topicIds}
          withAbsolutePositioning
          maxHeight={!topicIds ? 0 : topicIds.length * CHAT_SMALL_HEIGHT_PX}
          onKeyDown={handleTopicKeyDown}
        >
          {topicIds ? (
            topicIds.map((topicId, i) => (
              <ListItem
                key={`${forumId}_${topicId}`}
                className='chat-item-clickable force-rounded-corners small-icon topic-item'
                style={{ top: `${i * CHAT_SMALL_HEIGHT_PX}px` }}
                onClick={handleTopicClick}
                clickArg={topicId}
              >
                <TopicIcon
                  size={TOPIC_ICON_SIZE}
                  topic={topics[topicId]}
                  className='topic-icon'
                  letterClassName='topic-icon-letter'
                />
                <div dir='auto' className='fullName'>
                  {renderText(topics[topicId].title)}
                </div>
              </ListItem>
            ))
          ) : (
            <Loading />
          )}
        </InfiniteScroll>
      </>
    );
  }

  function renderChatList() {
    return (
      <>
        <InfiniteScroll
          elRef={containerRef}
          className='picker-list custom-scroll'
          items={viewportIds}
          onLoadMore={getMore}
          withAbsolutePositioning
          maxHeight={chatOrUserIds!.length * CHAT_SMALL_HEIGHT_PX}
          onKeyDown={handleKeyDown}
        >
          {viewportIds?.length ? (
            viewportIds.map((id, i) => (
              <ListItem
                key={id}
                className='chat-item-clickable force-rounded-corners small-icon'
                style={{
                  height: `${CHAT_SMALL_HEIGHT_PX}px`,
                  top: `${(viewportOffset + i) * CHAT_SMALL_HEIGHT_PX}px`,
                }}
                onClick={handleClick}
                clickArg={id}
              >
                {isUserId(id) ? (
                  <PrivateChatInfo
                    status={
                      id === currentUserId
                        ? String(t('SavedMessagesInfo'))
                        : undefined
                    }
                    userId={id}
                  />
                ) : (
                  <GroupChatInfo chatId={id} />
                )}
                {isShareLink && (
                  <Checkbox
                    label=''
                    name='user'
                    round
                    checked={selectedIds.includes(id)}
                  />
                )}
              </ListItem>
            ))
          ) : viewportIds && !viewportIds.length ? (
            <NothingFound text={t('TrySearch')} />
          ) : (
            <Loading />
          )}
        </InfiniteScroll>
      </>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      className='ChatOrUserPicker'
      onClose={onClose}
      onCloseAnimationEnd={onCloseAnimationEnd}
      header={renderHeader()}
    >
      <div className='modal-content'>
        <Transition activeKey={activeKey} name='slide-fade'>
          {() => {
            return activeKey === TOPIC_LIST_SLIDE
              ? renderTopicList()
              : renderChatList();
          }}
        </Transition>
      </div>
      {isShareLink && (
        <div className='modal-footer'>
          <SymbolMenuButton
            chatId={currentUserId!}
            threadId={MAIN_THREAD_ID}
            isMobile={isMobile}
            isReady={true}
            isSymbolMenuOpen={isSymbolMenuOpen}
            openSymbolMenu={openSymbolMenu}
            closeSymbolMenu={closeSymbolMenu}
            onCustomEmojiSelect={() => true}
            onRemoveSymbol={() => true}
            onEmojiSelect={insertTextAndUpdateCursor}
            canSendPlainText
            className='attachment-modal-symbol-menu with-menu-transitions'
          />
          <MessageInput
            elRef={inputRef}
            id={SEND_MODAL_INPUT_ID}
            editableInputId={EDITABLE_INPUT_MODAL_ID}
            isReady={true}
            threadId={MAIN_THREAD_ID}
            isAttachmentModalInput
            isActive={isOpen}
            getHtml={getHtml}
            getHtmlStr={getHtml}
            placeholder={t('AddComment')}
            onUpdate={setHtml}
            onSend={handleSendClick}
            canAutoFocus={false}
          />

          <Button onClick={handleSendClick}>Send</Button>
        </div>
      )}
    </Modal>
  );
};

export default memo(
  withGlobal<OwnProps>((global): StateProps => {
    const { requestedShare } = selectTabState(global);
    return {
      captionLimit: selectCurrentLimit(global, 'captionLength'),
      requestedShare,
    };
  })(ChatOrUserPicker)
);
